import { useState } from "react";
import { Card, Box, Typography, Button, Menu, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import muiStyles from "../../styles/muiStyles";
import ProjectModal from "./ProjectModal";
import {
  communityProjectsMockByLang,
  COMMUNITY_PROJECT_STATUSES,
} from "../../../../mocks/communityProjects";

// Visual styling for each status pill. Matches the existing badge pattern
// (see MyCommunityCard) — rgba bg + matching text color.
const STATUS_STYLES = {
  [COMMUNITY_PROJECT_STATUSES.ONGOING]: {
    background: "rgba(255, 151, 51, 0.16)",
    color: "rgba(255, 151, 51, 1)",
  },
  [COMMUNITY_PROJECT_STATUSES.ON_HOLD]: {
    background: "rgba(108, 138, 130, 0.16)",
    color: "rgba(90, 117, 110, 1)",
  },
  [COMMUNITY_PROJECT_STATUSES.REACHED_GOAL]: {
    background: "rgba(28, 160, 138, 0.16)",
    color: "rgba(28, 160, 138, 1)",
  },
  [COMMUNITY_PROJECT_STATUSES.COMPLETED]: {
    background: "rgba(70, 160, 145, 0.16)",
    color: "rgba(70, 160, 145, 1)",
  },
};

const STATUS_ORDER = [
  COMMUNITY_PROJECT_STATUSES.ONGOING,
  COMMUNITY_PROJECT_STATUSES.ON_HOLD,
  COMMUNITY_PROJECT_STATUSES.REACHED_GOAL,
  COMMUNITY_PROJECT_STATUSES.COMPLETED,
];

const StatusBadge = ({ status, label, onClick }) => {
  const styles =
    STATUS_STYLES[status] || STATUS_STYLES[COMMUNITY_PROJECT_STATUSES.ON_HOLD];
  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: "100px",
        padding: "4px 8px",
        borderRadius: "80px",
        fontSize: "12px",
        fontWeight: 400,
        textAlign: "center",
        // Keep the label on a single line; longer labels (e.g. "Reached goal")
        // grow past the 100px min rather than wrapping.
        whiteSpace: "nowrap",
        display: "inline-block",
        cursor: onClick ? "pointer" : "default",
        ...styles,
      }}
    >
      {label}
    </Box>
  );
};

const createCtaStyles = {
  textTransform: "none",
  fontSize: "15px",
  fontWeight: 500,
  color: "rgba(36, 139, 112, 1)",
  alignSelf: "flex-start",
  backgroundColor: "rgba(242, 243, 251, 1)",
  borderRadius: "8px",
  padding: "8px 16px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "rgba(36, 139, 112, 0.1)",
    boxShadow: "none",
  },
};

// Clean, borderless table styling to match the Figma (no row dividers / outline).
const GRID_TEMPLATE = "120px minmax(180px, 1fr) 180px 190px";

const headerCellSx = {
  fontSize: "14px",
  fontWeight: 400,
  color: "rgba(62, 63, 76, 0.6)",
  textAlign: "start",
};

const bodyCellSx = {
  fontSize: "15px",
  fontWeight: 400,
  color: "rgba(47, 43, 61, 0.9)",
  textAlign: "start",
  display: "flex",
  alignItems: "center",
  minWidth: 0,
};

const editButtonSx = {
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  color: "rgba(47, 43, 61, 0.9)",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  padding: 0,
  minWidth: "auto",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
};

const ManageCommunityProjects = () => {
  const { t, i18n } = useTranslation("page-community-settings");
  const { cardStyles } = muiStyles;

  const lang = (i18n.language || "en").toLowerCase().startsWith("he")
    ? "he"
    : "en";
  const [projects, setProjects] = useState(
    () => communityProjectsMockByLang[lang] || communityProjectsMockByLang.en,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingProject, setEditingProject] = useState(null);

  // Status-change menu state
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusTargetId, setStatusTargetId] = useState(null);

  const statusLabel = (status) =>
    t(`manage_community_projects.statuses.${status}`);

  // Demote any current ongoing project to on_hold, except the given id.
  const demoteOtherOngoing = (list, exceptId) =>
    list.map((p) =>
      p.status === COMMUNITY_PROJECT_STATUSES.ONGOING && p.id !== exceptId
        ? { ...p, status: COMMUNITY_PROJECT_STATUSES.ON_HOLD }
        : p,
    );

  const openCreate = () => {
    setModalMode("create");
    setEditingProject(null);
    setModalOpen(true);
  };

  const openEdit = (project) => {
    setModalMode("edit");
    setEditingProject(project);
    setModalOpen(true);
  };

  const handleModalSubmit = (formValues) => {
    if (modalMode === "create") {
      const newProject = {
        ...formValues,
        id: `proj-${Date.now()}`,
        raisedAmount: 0,
        status: COMMUNITY_PROJECT_STATUSES.ONGOING,
      };
      // New project becomes ongoing; demote the previous ongoing.
      setProjects((prev) => [
        ...demoteOtherOngoing(prev, newProject.id),
        newProject,
      ]);
    } else {
      // Edit: preserve existing status and raisedAmount.
      setProjects((prev) =>
        prev.map((p) =>
          p.id === editingProject.id
            ? {
                ...p,
                ...formValues,
                status: p.status,
                raisedAmount: p.raisedAmount,
              }
            : p,
        ),
      );
    }
    setModalOpen(false);
  };

  const openStatusMenu = (event, projectId) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusTargetId(projectId);
  };

  const closeStatusMenu = () => {
    setStatusAnchorEl(null);
    setStatusTargetId(null);
  };

  const handleStatusChange = (newStatus) => {
    setProjects((prev) => {
      let next = prev.map((p) =>
        p.id === statusTargetId ? { ...p, status: newStatus } : p,
      );
      // Changing to ongoing demotes the previous ongoing.
      if (newStatus === COMMUNITY_PROJECT_STATUSES.ONGOING) {
        next = demoteOtherOngoing(next, statusTargetId);
      }
      return next;
    });
    closeStatusMenu();
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString();

  const getPercent = (raised, goal) =>
    goal > 0 ? parseFloat(((raised / goal) * 100).toFixed(2)) : 0;

  return (
    <Card sx={cardStyles}>
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 500,
          color: "#3E3F4C",
          mb: "24px",
        }}
      >
        {t("manage_community_projects.title")}
      </Typography>

      {projects.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            py: "32px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 400,
              color: "rgba(47, 43, 61, 0.7)",
            }}
          >
            {t("manage_community_projects.empty_state")}
          </Typography>
          <Button
            onClick={openCreate}
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 500,
              borderRadius: "8px",
              backgroundColor: "rgba(37, 124, 109, 1)",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(37, 124, 109, 0.9)",
                boxShadow: "none",
              },
            }}
          >
            {t("manage_community_projects.create_cta")}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: "720px", maxWidth: "880px" }}>
              {/* Header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_TEMPLATE,
                  columnGap: "16px",
                  pb: "12px",
                }}
              >
                <Typography sx={headerCellSx}>
                  {t("manage_community_projects.columns.goal")}
                </Typography>
                <Typography sx={headerCellSx}>
                  {t("manage_community_projects.columns.name")}
                </Typography>
                <Typography sx={headerCellSx}>
                  {t("manage_community_projects.columns.goal_status")}
                </Typography>
                <Typography sx={headerCellSx}>
                  {t("manage_community_projects.columns.actions")}
                </Typography>
              </Box>

              {/* Data rows */}
              {projects.map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_TEMPLATE,
                    columnGap: "16px",
                    alignItems: "center",
                    py: "14px",
                  }}
                >
                  <Box sx={bodyCellSx}>{formatNumber(row.fundingGoal)}</Box>
                  <Box sx={bodyCellSx}>{row.name}</Box>
                  <Box sx={bodyCellSx}>
                    {`${formatNumber(row.raisedAmount)} (${getPercent(
                      row.raisedAmount,
                      row.fundingGoal,
                    )}%)`}
                  </Box>
                  <Box sx={{ ...bodyCellSx, gap: "16px" }}>
                    <StatusBadge
                      status={row.status}
                      label={statusLabel(row.status)}
                      onClick={(e) => openStatusMenu(e, row.id)}
                    />
                    <Button
                      variant="text"
                      onClick={() => openEdit(row)}
                      sx={editButtonSx}
                    >
                      {t("manage_community_projects.edit")}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Button onClick={openCreate} variant="text" sx={createCtaStyles}>
            {t("manage_community_projects.create_cta")}
          </Button>
        </Box>
      )}

      {/* Status-change menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: "5px",
            borderRadius: "12px",
            boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.16)",
            padding: "4px 8px",
            minWidth: "180px",
          },
        }}
      >
        {STATUS_ORDER.map((status) => (
          <MenuItem
            key={status}
            onClick={() => handleStatusChange(status)}
            sx={{
              borderRadius: "8px",
              fontSize: "14px",
              "&:hover": { backgroundColor: "rgba(245, 246, 250, 1)" },
            }}
          >
            <StatusBadge status={status} label={statusLabel(status)} />
          </MenuItem>
        ))}
      </Menu>

      {modalOpen && (
        <ProjectModal
          open={modalOpen}
          mode={modalMode}
          project={editingProject}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
        />
      )}
    </Card>
  );
};

export default ManageCommunityProjects;
